import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ActionSheetController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { AuditNodeViewModel } from 'src/app/business/swodoc/view-models/audit-node-view-model';
import { LightboxService } from 'src/app/shared/components/common/lightbox/lightbox.service';
import { v4 } from 'uuid';

import { ATTACHMENT_LOCAL_PREFIX } from '@services/attachment/attachment-service.interface';
import { getMarkerJsArea } from '../../../../services/marker-js-generator';
import { IAttachmentWithData } from '../../document-attachment.service';
import { DocumentEditService } from '../../document-edit.service';

@Component({
  selector: 'swodoc-node-picture',
  templateUrl: 'swodoc-node-picture.html',
  styleUrls: ['swodoc-node-picture.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SwodocNodePicture implements OnInit {
  @Input() public node: AuditNodeViewModel;
  @Input() public documentEditService: DocumentEditService;
  @Output() public changed: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild('markerImageElement', { static: true }) public markerImageElementRef: ElementRef;
  public busy: boolean;
  public image: Partial<IAttachmentWithData>;
  public markerImage: string;

  constructor(
    private _lightbox: LightboxService,
    private _cdr: ChangeDetectorRef,
    private _actionSheetCtrl: ActionSheetController,
    private _translate: TranslateService,
    private _changeDetectionRef: ChangeDetectorRef
  ) {}

  ngOnInit() {
    if (this.node.responses?.[0]?.value) {
      const images = this.documentEditService.getImagesForNodeId(this.node.responses[0].value.toString());
      this.image = images[0];
    } else {
      const images = this.documentEditService.getImagesForNodeId(this.node.options);
      this.image = images[0];
    }
  }

  public async showEditImageActionSheet() {
    if (!this.node.allowDrawPicture) {
      await this._lightbox.show([this.image.data], 0);
      return;
    }
    const action = await this.showImageActionSheet();
    if (!action) {
      return;
    } else {
      switch (action) {
        case 'show':
          await this._lightbox.show([this.image.data], 0, true);
          break;
        case 'edit':
          const result = await this.openMarkerField();
          if (result) {
            this.saveImage(result);
          }
          break;
      }
    }
  }
  private saveImage(image) {
    const imageToSave = image;
    if (imageToSave) {
      if (this.image.metadata.auditNodeId) {
        this.documentEditService.removeImage(this.image._id);
      }
      const newImage = {
        _id: `${ATTACHMENT_LOCAL_PREFIX}${v4()}`,
        metadata: {
          auditNodeId: this.node.id,
          type: 101, // SwodocPicture
        },
        data: imageToSave,
      } as Partial<IAttachmentWithData>;
      this.documentEditService.addImage(newImage);
      this.image = newImage;
      this.node.responses = [{ value: this.node.id }];
      this._cdr.markForCheck();
    }
  }

  showImageActionSheet() {
    return new Promise(async resolve => {
      const actionSheet = await this._actionSheetCtrl.create({
        buttons: [
          {
            text: this._translate.instant('audit.node.image.edit_image_actionsheet.view'),
            handler: () => {
              resolve('show');
            },
          },
          {
            text: this._translate.instant('audit.node.image.edit_image_actionsheet.edit'),
            handler: () => {
              resolve('edit');
            },
          },
          {
            text: this._translate.instant('audit.node.image.edit_image_actionsheet.cancel'),
            handler: () => {},
          },
        ],
      });
      await actionSheet.present();
    });
  }
  private async openMarkerField(): Promise<string> {
    return new Promise(resolve => {
      this.markerImage = this.image.data;
      this._changeDetectionRef.detectChanges();
      setTimeout(() => {
        const markerArea = getMarkerJsArea(this.markerImageElementRef);
        markerArea.addRenderEventListener(dataUrl => resolve(dataUrl));
        markerArea.addCloseEventListener(() => resolve(null));
        markerArea.show();
      });
    });
  }
}
